import { CountryAutocomplete } from "@/components/ui/CountryAutocomplete";
import {
  createSegment,
  deleteSegment,
  deleteSegmentDocument,
  SegmentDocument,
  TravelSegment,
  updateSegment,
  uploadSegmentDocument,
} from "@/services/api/travels";
import { useI18nStore } from "@/stores/i18nStore";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import type { Translation } from "@/types/i18n";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Edit2,
  FileText,
  Paperclip,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import React, { useRef, useState } from "react";
import { toast } from "react-hot-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SegmentInlineListProps {
  travelId: string;
  segments: TravelSegment[];
  onSegmentsChange: (segments: TravelSegment[]) => void;
  canUploadDocuments?: boolean; // STANDARD+ feature
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const SegmentInlineList: React.FC<SegmentInlineListProps> = ({
  travelId,
  segments,
  onSegmentsChange,
  canUploadDocuments = false,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingSegment, setEditingSegment] = useState<TravelSegment | null>(
    null,
  );
  const [showAddForm, setShowAddForm] = useState(false);

  const sorted = [...segments].sort(
    (a, b) =>
      new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
  );

  const handleDelete = async (segmentId: string) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await deleteSegment(travelId, segmentId);
      onSegmentsChange(segments.filter((s) => s._id !== segmentId));
      toast.success(t.deletedSuccessfully);
    } catch {
      toast.error(t.error);
    }
  };

  const handleSaved = (seg: TravelSegment) => {
    const exists = segments.find((s) => s._id === seg._id);
    if (exists) {
      onSegmentsChange(segments.map((s) => (s._id === seg._id ? seg : s)));
    } else {
      onSegmentsChange([...segments, seg]);
    }
    setEditingSegment(null);
    setShowAddForm(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          {t.tripSegments}
        </h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          {t.addSegment}
        </button>
      </div>

      {sorted.length === 0 && !showAddForm && (
        <div className="rounded-xl border-2 border-dashed border-gray-200 p-6 text-center">
          <p className="text-sm text-gray-500">{t.noSegmentsYet}</p>
          <p className="text-xs text-gray-400 mt-1">
            {t.addSegmentsDescription}
          </p>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <SegmentForm
          travelId={travelId}
          onSaved={handleSaved}
          onCancel={() => setShowAddForm(false)}
          t={t}
        />
      )}

      {/* Segment List */}
      {sorted.map((seg) => (
        <div
          key={seg._id}
          className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
        >
          {/* Header */}
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
            onClick={() =>
              setExpandedId(expandedId === seg._id ? null : seg._id)
            }
          >
            <div className="flex items-center gap-3 min-w-0">
              <SegmentTypeIcon type={seg.segmentType} />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-800">
                  {seg.segmentTitle}
                </p>
                <p className="text-xs text-gray-400">
                  {seg.startLocation} → {seg.endLocation} ·{" "}
                  {seg.startDateTime ? fmt.date(seg.startDateTime) : "—"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <HealthStatusBadge status={seg.healthStatus} />
              {expandedId === seg._id ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </button>

          {/* Expanded Content */}
          {expandedId === seg._id && (
            <div className="border-t border-gray-100 px-4 py-3 space-y-4 bg-gray-50">
              {editingSegment?._id === seg._id ? (
                <SegmentForm
                  travelId={travelId}
                  segment={seg}
                  onSaved={handleSaved}
                  onCancel={() => setEditingSegment(null)}
                  t={t}
                />
              ) : (
                <>
                  <SegmentDetails seg={seg} t={t} />

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => setEditingSegment(seg)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                      {t.edit}
                    </button>
                    <button
                      onClick={() => handleDelete(seg._id)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {t.delete}
                    </button>
                  </div>

                  {/* Documents */}
                  <DocumentSection
                    travelId={travelId}
                    segmentId={seg._id}
                    documents={
                      (seg as TravelSegment & { documents?: SegmentDocument[] })
                        .documents ?? []
                    }
                    onDocumentsChange={(docs) => {
                      const updated = {
                        ...seg,
                        documents: docs,
                      } as TravelSegment & { documents: SegmentDocument[] };
                      onSegmentsChange(
                        segments.map((s) => (s._id === seg._id ? updated : s)),
                      );
                    }}
                    canUpload={canUploadDocuments}
                    t={t}
                  />
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Segment Form ─────────────────────────────────────────────────────────────

interface SegmentFormProps {
  travelId: string;
  segment?: TravelSegment;
  onSaved: (seg: TravelSegment) => void;
  onCancel: () => void;
  t: Translation;
}

const SegmentForm: React.FC<SegmentFormProps> = ({
  travelId,
  segment,
  onSaved,
  onCancel,
  t,
}) => {
  const isEdit = !!segment;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    segmentTitle: segment?.segmentTitle ?? "",
    segmentType: (segment?.segmentType ??
      "transfer") as TravelSegment["segmentType"],
    segmentOperator: segment?.segmentOperator ?? "",
    bookingReference: segment?.bookingReference ?? "",
    startDateTime: segment?.startDateTime ?? "",
    startLocation: segment?.startLocation ?? "",
    startCountry: segment?.startCountry ?? "",
    endDateTime: segment?.endDateTime ?? "",
    endLocation: segment?.endLocation ?? "",
    endCountry: segment?.endCountry ?? "",
    transportationType: segment?.transportationType ?? "",
    accommodationType: segment?.accommodationType ?? "",
    notes: segment?.notes ?? "",
  });

  const update = (patch: Partial<typeof form>) =>
    setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let result: TravelSegment;
      if (isEdit && segment) {
        result = await updateSegment(travelId, segment._id, form);
      } else {
        result = await createSegment(travelId, {
          segmentTitle: form.segmentTitle,
          segmentType: form.segmentType,
          startDateTime: form.startDateTime,
          startLocation: form.startLocation,
          startCountry: form.startCountry,
          endDateTime: form.endDateTime,
          endLocation: form.endLocation,
          endCountry: form.endCountry,
          segmentOperator: form.segmentOperator || undefined,
          bookingReference: form.bookingReference || undefined,
          transportationType: form.transportationType || undefined,
          accommodationType: form.accommodationType || undefined,
          notes: form.notes || undefined,
        });
      }
      toast.success(t.savedSuccessfully);
      onSaved(result);
    } catch {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-indigo-800">
          {isEdit ? t.editSegment : t.addSegment}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.segmentTitle} *
          </label>
          <input
            required
            value={form.segmentTitle}
            onChange={(e) => update({ segmentTitle: e.target.value })}
            className={inputCls}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.segmentType}
          </label>
          <select
            value={form.segmentType}
            onChange={(e) =>
              update({
                segmentType: e.target.value as TravelSegment["segmentType"],
              })
            }
            className={inputCls}
          >
            <option value="transfer">{t.segmentTypeTransfer}</option>
            <option value="destination">{t.segmentTypeDestination}</option>
            <option value="accommodation">{t.segmentTypeAccommodation}</option>
            <option value="activity">{t.segmentTypeActivity}</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.operator}
          </label>
          <input
            value={form.segmentOperator}
            onChange={(e) => update({ segmentOperator: e.target.value })}
            className={inputCls}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.startDateTime} *
          </label>
          <input
            required
            type="datetime-local"
            value={form.startDateTime}
            onChange={(e) => update({ startDateTime: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.endDateTime} *
          </label>
          <input
            required
            type="datetime-local"
            value={form.endDateTime}
            onChange={(e) => update({ endDateTime: e.target.value })}
            className={inputCls}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.startLocation} *
          </label>
          <input
            required
            value={form.startLocation}
            onChange={(e) => update({ startLocation: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.endLocation} *
          </label>
          <input
            required
            value={form.endLocation}
            onChange={(e) => update({ endLocation: e.target.value })}
            className={inputCls}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.startCountry}
          </label>
          <CountryAutocomplete
            value={form.startCountry}
            onChange={(v) => update({ startCountry: v })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.endCountry}
          </label>
          <CountryAutocomplete
            value={form.endCountry}
            onChange={(v) => update({ endCountry: v })}
          />
        </div>
      </div>

      {form.segmentType === "transfer" && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.transportationType}
          </label>
          <select
            value={form.transportationType}
            onChange={(e) => update({ transportationType: e.target.value })}
            className={inputCls}
          >
            <option value="">{t.selectOption}</option>
            <option value="flight">{t.transportFlight}</option>
            <option value="train">{t.transportTrain}</option>
            <option value="bus">{t.transportBus}</option>
            <option value="car">{t.transportCar}</option>
            <option value="ship">{t.transportShip}</option>
            <option value="other">{t.other}</option>
          </select>
        </div>
      )}

      {form.segmentType === "accommodation" && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t.accommodationType}
          </label>
          <select
            value={form.accommodationType}
            onChange={(e) => update({ accommodationType: e.target.value })}
            className={inputCls}
          >
            <option value="">{t.selectOption}</option>
            <option value="hotel">{t.accommodationHotel}</option>
            <option value="hostel">{t.accommodationHostel}</option>
            <option value="apartment">{t.accommodationApartment}</option>
            <option value="cruise_cabin">{t.accommodationCruiseCabin}</option>
            <option value="other">{t.other}</option>
          </select>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {t.cancel}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving ? "..." : t.save}
        </button>
      </div>
    </form>
  );
};

// ─── Document Section ─────────────────────────────────────────────────────────

interface DocumentSectionProps {
  travelId: string;
  segmentId: string;
  documents: SegmentDocument[];
  onDocumentsChange: (docs: SegmentDocument[]) => void;
  canUpload: boolean;
  t: Translation;
}

const DocumentSection: React.FC<DocumentSectionProps> = ({
  travelId,
  segmentId,
  documents,
  onDocumentsChange,
  canUpload,
  t,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] =
    useState<SegmentDocument["category"]>("ticket");

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const doc = await uploadSegmentDocument(
        travelId,
        segmentId,
        file,
        category,
      );
      onDocumentsChange([...documents, doc]);
      toast.success(t.documentUploaded);
    } catch (error) {
      // A refused type says which types are accepted, which the generic
      // failure message cannot.
      const body = (
        error as {
          response?: { data?: { error?: { code?: string; message?: string } } };
        }
      )?.response?.data?.error;
      toast.error(
        body?.code === "UNSUPPORTED_FILE_TYPE" && body.message
          ? body.message
          : t.documentUploadFailed,
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await deleteSegmentDocument(travelId, segmentId, docId);
      onDocumentsChange(documents.filter((d) => d.id !== docId));
      toast.success(t.documentDeleted);
    } catch {
      toast.error(t.error);
    }
  };

  if (!canUpload && documents.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-gray-200 pt-3">
      <div className="flex items-center gap-2">
        <Paperclip className="h-3.5 w-3.5 text-gray-400" />
        <p className="text-xs font-medium text-gray-600">
          {t.segmentDocuments}
        </p>
      </div>

      {documents.length > 0 && (
        <ul className="space-y-1">
          {documents.map((doc) => (
            <li
              key={doc.id}
              className="flex items-center justify-between rounded-lg bg-white border border-gray-200 px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate text-xs text-indigo-600 hover:underline"
                >
                  {doc.filename}
                </a>
                <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                  {docCategoryLabel(doc.category, t)}
                </span>
              </div>
              <button
                onClick={() => handleDelete(doc.id)}
                className="ml-2 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {canUpload && (
        <div className="flex items-center gap-2">
          <select
            value={category}
            onChange={(e) =>
              setCategory(e.target.value as SegmentDocument["category"])
            }
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="ticket">{t.docCategoryTicket}</option>
            <option value="booking">{t.docCategoryBooking}</option>
            <option value="voucher">{t.docCategoryVoucher}</option>
            <option value="insurance">{t.docCategoryInsurance}</option>
            <option value="other">{t.docCategoryOther}</option>
          </select>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <Plus className="h-3 w-3" />
            {uploading ? "..." : t.uploadSegmentDocument}
          </button>
          <span className="text-xs text-gray-400">{t.maxFileSizeHint}</span>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components / Helpers ─────────────────────────────────────────────────

const SegmentDetails: React.FC<{ seg: TravelSegment; t: Translation }> = ({
  seg,
  t,
}) => {
  const fmt = useLocaleFormat();
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
      <div>
        <dt className="text-gray-400">{t.startDateTime}</dt>
        <dd className="font-medium text-gray-700">
          {seg.startDateTime ? fmt.dateTime(seg.startDateTime) : "—"}
        </dd>
      </div>
      <div>
        <dt className="text-gray-400">{t.endDateTime}</dt>
        <dd className="font-medium text-gray-700">
          {seg.endDateTime ? fmt.dateTime(seg.endDateTime) : "—"}
        </dd>
      </div>
      <div>
        <dt className="text-gray-400">{t.startCountry}</dt>
        <dd className="font-medium text-gray-700">{seg.startCountry || "—"}</dd>
      </div>
      <div>
        <dt className="text-gray-400">{t.endCountry}</dt>
        <dd className="font-medium text-gray-700">{seg.endCountry || "—"}</dd>
      </div>
      {seg.transportationType && (
        <div>
          <dt className="text-gray-400">{t.transportationType}</dt>
          <dd className="font-medium text-gray-700">
            {seg.transportationType}
          </dd>
        </div>
      )}
      {seg.accommodationType && (
        <div>
          <dt className="text-gray-400">{t.accommodationType}</dt>
          <dd className="font-medium text-gray-700">{seg.accommodationType}</dd>
        </div>
      )}
      {seg.notes && (
        <div className="col-span-2">
          <dt className="text-gray-400">{t.notes}</dt>
          <dd className="font-medium text-gray-700">{seg.notes}</dd>
        </div>
      )}
    </dl>
  );
};

const SegmentTypeIcon: React.FC<{ type: TravelSegment["segmentType"] }> = ({
  type,
}) => {
  const icons: Record<string, string> = {
    transfer: "✈️",
    destination: "📍",
    accommodation: "🏨",
    activity: "⚡",
  };
  return <span className="text-base shrink-0">{icons[type] ?? "📋"}</span>;
};

const HealthStatusBadge: React.FC<{
  status?: TravelSegment["healthStatus"];
}> = ({ status }) => {
  if (!status) return null;
  const cls =
    status === "ready"
      ? "bg-green-100 text-green-700"
      : status === "warning"
        ? "bg-red-100 text-red-600"
        : "bg-amber-100 text-amber-700";
  const icon =
    status === "warning" ? <AlertTriangle className="h-3 w-3" /> : null;
  return (
    <span
      className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {icon}
      {status}
    </span>
  );
};

function docCategoryLabel(
  cat: SegmentDocument["category"],
  t: Translation,
): string {
  const map: Record<SegmentDocument["category"], string> = {
    ticket: t.docCategoryTicket,
    booking: t.docCategoryBooking,
    voucher: t.docCategoryVoucher,
    insurance: t.docCategoryInsurance,
    other: t.docCategoryOther,
  };
  return map[cat] ?? cat;
}
